"use client";

import { useSearchParams } from "next/navigation";
import { useMemo } from "react";

import { isProduction } from "@/env";

// Dev-only state overrides for the Card Vault page.
//
// Usage:
//   /card-vault?cv_state=<base64-json>
//
// Where the JSON shape is CardvaultOverride below. Also supports
// localStorage["cv_state"] (URL param wins when both are present).
//
// This entire hook short-circuits to null in production builds. When the
// campaign directory is deleted, this file and every call site go with it.

const OVERRIDE_PARAM = "cv_state";
const OVERRIDE_STORAGE_KEY = "cv_state";

export type CardvaultPhase = "pre_reveal" | "revealed" | "draw_pending" | "drawn";

export interface CardvaultOverride {
  // Weeks this wallet should be treated as having won.
  // UI will synthesize WinnerForUI entries for the connected wallet.
  wins?: number[];
  // Weeks already claimed, shortcuts the /api/card-vault/claims/:owner response.
  claimed?: number[];
  // Force enrollment status (otherwise uses useCardvaultEnrollment).
  enrolled?: boolean;
  // Force a campaign phase for countdown/reveal UI.
  phase?: CardvaultPhase;
  // Freeze "now" for deterministic countdowns. ISO string.
  now?: string;
}

function decode(raw: string | null): CardvaultOverride | null {
  if (!raw) return null;
  try {
    const json = typeof atob === "function" ? atob(raw) : Buffer.from(raw, "base64").toString();
    return JSON.parse(json) as CardvaultOverride;
  } catch {
    return null;
  }
}

export function useCardvaultOverride(): CardvaultOverride | null {
  const searchParams = useSearchParams();
  const urlValue = searchParams.get(OVERRIDE_PARAM);

  return useMemo(() => {
    if (isProduction) return null;

    const fromUrl = decode(urlValue);
    if (fromUrl) return fromUrl;

    if (typeof window === "undefined") return null;
    return decode(window.localStorage.getItem(OVERRIDE_STORAGE_KEY));
  }, [urlValue]);
}
